import type { AppInfo } from "@codeshare/protocol";

/** Compares semver strings (major.minor.patch[-prerelease]); prereleases sort before releases. */
export function compareSemver(a: string, b: string): number {
  const parse = (v: string) => {
    const [core = "", pre] = v.trim().replace(/^v/, "").split("-", 2);
    const nums = core.split(".").map((n) => Number.parseInt(n, 10) || 0);
    return { nums: [nums[0] ?? 0, nums[1] ?? 0, nums[2] ?? 0], pre };
  };
  const x = parse(a);
  const y = parse(b);
  for (let i = 0; i < 3; i++) {
    const diff = (x.nums[i] ?? 0) - (y.nums[i] ?? 0);
    if (diff !== 0) return Math.sign(diff);
  }
  if (x.pre === y.pre) return 0;
  if (x.pre === undefined) return 1;
  if (y.pre === undefined) return -1;
  return x.pre < y.pre ? -1 : 1;
}

/** >0 when `a` is the newer build: semver first, then build time for equal versions. */
export function compareApp(
  a: Pick<AppInfo, "version" | "builtAt">,
  b: Pick<AppInfo, "version" | "builtAt">,
): number {
  return compareSemver(a.version, b.version) || Math.sign(a.builtAt - b.builtAt);
}

export type UpdateAdvice =
  | { kind: "none" }
  /** A refresh will load the newer build the peer is using. */
  | { kind: "update-available"; peerName: string; peerVersion: string; localVersion: string }
  /** The peer is newer, but this site hasn't deployed that build (yet). */
  | { kind: "not-deployed"; peerName: string; peerVersion: string }
  /** The peer runs a newer build from another site (a fork or self-host): informational only. */
  | { kind: "other-origin"; peerName: string; peerVersion: string; origin: string };

export interface PeerBuild {
  name: string;
  app: AppInfo;
}

/** The newest build among peers that is newer than ours, if any. */
export function newestPeer(local: AppInfo, peers: PeerBuild[]): PeerBuild | undefined {
  let newest: PeerBuild | undefined;
  for (const peer of peers) {
    if (compareApp(peer.app, local) <= 0) continue;
    if (!newest || compareApp(peer.app, newest.app) > 0) newest = peer;
  }
  return newest;
}

/**
 * What to tell the user about versions (docs/PRD.md §7). Only the older side is prompted; a
 * refresh is only suggested when this site's own version.json shows it would actually help.
 */
export async function adviseUpdate(
  local: AppInfo,
  peers: PeerBuild[],
  fetchDeployed: () => Promise<Pick<AppInfo, "version" | "builtAt"> | undefined>,
): Promise<UpdateAdvice> {
  const peer = newestPeer(local, peers);
  if (!peer) return { kind: "none" };
  const base = { peerName: peer.name, peerVersion: peer.app.version };
  if (peer.app.origin !== local.origin)
    return { kind: "other-origin", ...base, origin: peer.app.origin };
  let deployed: Pick<AppInfo, "version" | "builtAt"> | undefined;
  try {
    deployed = await fetchDeployed();
  } catch {
    deployed = undefined;
  }
  if (deployed && compareApp(deployed, local) > 0) {
    return { kind: "update-available", ...base, localVersion: local.version };
  }
  return { kind: "not-deployed", ...base };
}
